import { useCallback, useState } from "react";
import {
  CampaignServiceError,
  createOpportunity,
  updateOpportunity,
} from "../api";
import {
  CreateOpportunityRequest,
  CreateOpportunityResponse,
  UpdateOpportunityRequest,
  UpdateOpportunityResponse,
} from "../types";

type CreateOpportunityDispatchAction = {
  type: "opportunity/create";
  payload: {
    programId: string;
    payload: Omit<CreateOpportunityRequest, "programId">;
    onSuccess?: (result: CreateOpportunityResponse) => void;
  };
};

type UpdateOpportunityDispatchAction = {
  type: "opportunity/update";
  payload: {
    programId: string;
    opportunityId: string;
    payload: UpdateOpportunityRequest;
    onSuccess?: (result: UpdateOpportunityResponse) => void;
  };
};

type OpportunityDispatchAction =
  | CreateOpportunityDispatchAction
  | UpdateOpportunityDispatchAction;

type OpportunityDispatchResultMap = {
  "opportunity/create": CreateOpportunityResponse;
  "opportunity/update": UpdateOpportunityResponse;
};

type OpportunityDispatchResult<TAction extends OpportunityDispatchAction> =
  TAction extends CreateOpportunityDispatchAction
    ? OpportunityDispatchResultMap["opportunity/create"]
    : TAction extends UpdateOpportunityDispatchAction
      ? OpportunityDispatchResultMap["opportunity/update"]
      : never;

interface UseOpportunityDispatcherResult {
  dispatch: <TAction extends OpportunityDispatchAction>(
    action: TAction,
  ) => Promise<OpportunityDispatchResult<TAction>>;
  saving: boolean;
  error: string | null;
}

export function useOpportunityDispatcher(): UseOpportunityDispatcherResult {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dispatch = useCallback(
    async <TAction extends OpportunityDispatchAction>(
      action: TAction,
    ): Promise<OpportunityDispatchResult<TAction>> => {
      setSaving(true);
      setError(null);

      try {
        switch (action.type) {
          case "opportunity/create": {
            const createdOpportunity = await createOpportunity(
              action.payload.programId,
              action.payload.payload,
            );

            action.payload.onSuccess?.(createdOpportunity);

            return createdOpportunity as OpportunityDispatchResult<TAction>;
          }

          case "opportunity/update": {
            const updatedOpportunity = await updateOpportunity(
              action.payload.programId,
              action.payload.opportunityId,
              action.payload.payload,
            );

            action.payload.onSuccess?.(updatedOpportunity);

            return updatedOpportunity as OpportunityDispatchResult<TAction>;
          }

          default: {
            const exhaustiveCheck: never = action;
            throw new Error(
              `Unsupported opportunity action: ${JSON.stringify(exhaustiveCheck)}`,
            );
          }
        }
      } catch (dispatchError) {
        const message =
          dispatchError instanceof CampaignServiceError ||
          dispatchError instanceof Error
            ? dispatchError.message
            : "Failed to process opportunity action.";

        setError(message);
        throw dispatchError;
      } finally {
        setSaving(false);
      }
    },
    [],
  );

  return {
    dispatch,
    saving,
    error,
  };
}
